import { AgoraRTCProvider, useRTCClient } from 'agora-rtc-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { videoCallHungUp, videoCallJoined } from '@/api/consultations'
import { Button } from '@/components/Button'
import { Ringing } from '@/components/icons/Ringing'
import { WarningIcon } from '@/components/icons/WarningIcon'
import { useConsultation } from '@/hooks/useConsultation'
import { useMediaPermissions } from '@/hooks/useMediaPermissions'
import { useUser } from '@/hooks/useUser'
import { getSlugTranslation } from '@/locale/utils'

import AgoraManager from './AgoraManager'
import { agoraClient } from './AgoraManager/agora-config'

export const VideoCall = () => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { consultation, updateConsultation } = useConsultation()
  const [joined, setJoined] = useState(false)
  const { hasAllPermissionsGranted, hasNotAllowedPermissions, getUnblockInstructions } = useMediaPermissions()

  const agoraEngine = useRTCClient(agoraClient)

  const professional = consultation?.professional || {
    title: '',
    name: '',
    gender: ''
  }

  const hangUp = async () => {
    try {
      const consultationHungUp = await videoCallHungUp({
        uniqueId: user?.data?.uniqueId!
      })
      updateConsultation(consultationHungUp)
      setJoined(false)
    } catch (err) {
      console.error(err)
    }
  }

  const handleJoin = async () => {
    try {
      setJoined(true)
      const consultationJoined = await videoCallJoined({
        uniqueId: user?.data?.uniqueId!
      })
      updateConsultation(consultationJoined)
    } catch (err) {
      console.error(err)
    }
  }

  if (!professional || !consultation?.video) return null

  if (joined) {
    const {
      video: { room, token, appId, uuid }
    } = consultation
    const config = {
      channel: room,
      uid: uuid,
      token,
      appid: appId
    }

    return (
      <div className="flex grow flex-col">
        <AgoraRTCProvider client={agoraEngine}>
          <AgoraManager config={config} handleHangUp={hangUp} />
        </AgoraRTCProvider>
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col items-center gap-8 p-8" data-testid="join-container">
      <h2 className="text-center text-2xl font-bold">
        {t([`video.calling.${professional.title}_${professional.gender}`, `video.calling.person_${professional.gender}`], {
          name: professional.name
        })}
      </h2>

      <Ringing className="text-primary" />

      <Button onClick={handleJoin} customClassName="flex gap-4 items-center justify-center w-full" disabled={!hasAllPermissionsGranted}>
        {getSlugTranslation(consultation.video.patientJoinedAt ? 'video.recalling.button' : 'video.calling.button')}
      </Button>

      {hasNotAllowedPermissions && (
        <div className="flex flex-col gap-4 rounded-lg bg-white p-4 text-sm shadow-sm">
          <div className="flex flex-row items-center gap-4">
            <WarningIcon className="text-error h-8 w-8" />
            <p className="text-error font-bold">{t('error.camera_microphone.no_camera_no_microphone')}</p>
          </div>

          <div className="w-full">
            <p className="font-bold">{t('error.camera_microphone.to_enable')}</p>
            <p className="whitespace-break-spaces">{t(getUnblockInstructions())}</p>
          </div>
        </div>
      )}

      <p className="text-gray-text rounded-lg bg-white p-2 text-center text-sm">{getSlugTranslation('video.calling.not_refresh')}</p>
    </div>
  )
}
